import { FeatureBox3D } from '../procedural/FeatureBox3D'
import { FeatureCylinder3D } from '../procedural/FeatureCylinder3D'
import { FeatureLabel3D } from '../procedural/FeatureLabel3D'
import { SurfaceFeature3D } from '../SurfaceFeature3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  BittNominalFittingSize,
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoShipDeckBitt,
} from 'types'

/**
 * A component representing a bitt on the surface of a ship deck component
 * @param model             The bitt model (from the asset model)
 * @constructor
 */
export const DeckBittFeature3D: React.FC<{
  model: GeckoShipDeckBitt
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()

  const getSize = (nominalFittingSize: BittNominalFittingSize) => {
    switch (nominalFittingSize) {
      case BittNominalFittingSize.US4:
        return {
          width: 0.4191,
          height: 0.1905,
          radius: 0.1143 / 2,
          barrelHeight: 0.2921,
        }
      case BittNominalFittingSize.US6:
        return {
          width: 0.612775,
          height: 0.282575,
          radius: 0.168275 / 2,
          barrelHeight: 0.38735,
        }
      case BittNominalFittingSize.US8:
        return {
          width: 0.777875,
          height: 0.346075,
          radius: 0.219075 / 2,
          barrelHeight: 0.4191,
        }
      case BittNominalFittingSize.US10:
        return {
          width: 0.99695,
          height: 0.43815,
          radius: 0.27305 / 2,
          barrelHeight: 0.51435,
        }
      case BittNominalFittingSize.US12:
        return {
          width: 1.14935,
          height: 0.51435,
          radius: 0.32385 / 2,
          barrelHeight: 0.62865,
        }
      default:
        return {
          width: 0.4191,
          height: 0.1905,
          radius: 0.1143 / 2,
          barrelHeight: 0.2921,
        }
    }
  }

  const size = getSize(model.nominalFittingSize)

  const width = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.width,
      GeckoMeasurementUnit.Meters
    )
  }, [size.width])

  const height = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.height,
      GeckoMeasurementUnit.Meters
    )
  }, [size.height])

  const radius = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.radius,
      GeckoMeasurementUnit.Meters
    )
  }, [size.radius])

  const barrelHeight = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.barrelHeight,
      GeckoMeasurementUnit.Meters
    )
  }, [size.barrelHeight])

  const barrelXPos = React.useMemo(() => {
    return width.scale(0.25).num
  }, [width])

  const { labelCoords, positionLabelLeft } = React.useMemo(() => {
    const positionLabelLeft =
      model.geometry.x.num < bounds.start.x.plus(bounds.end.x).scale(0.5).num

    const x = positionLabelLeft
      ? model.geometry.x.minus(width.scale(0.5))
      : model.geometry.x.plus(width.scale(0.5))

    const y = model.geometry.y
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelLeft }
  }, [model, bounds, width])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelLeft ? 'right' : 'left'}
      />
      <SurfaceFeature3D model={model}>
        <FeatureBox3D
          feature={model}
          position={[0, 0, 0]}
          width={width.num}
          depth={0.1}
          height={height.num}
          debugColor={'purple'}
        />
        <FeatureCylinder3D
          feature={model}
          position={[-barrelXPos, 0, 0]}
          radius={radius.num}
          height={barrelHeight.num}
          debugColor={'purple'}
        />
        <FeatureCylinder3D
          feature={model}
          position={[barrelXPos, 0, 0]}
          radius={radius.num}
          height={barrelHeight.num}
          debugColor={'purple'}
        />
      </SurfaceFeature3D>
    </>
  )
}
